import React, { useEffect, useMemo, useState } from "react";
import { Search, Layers, Copy } from "lucide-react";
import api from "../lib/api";
import CardFace from "../components/CardFace";
import CardDetailModal from "../components/CardDetailModal";
import { rarityMeta, FAMILY_LABEL } from "../lib/helpers";

const STATUS = [
  { id: "tutte", label: "Tutte" },
  { id: "possedute", label: "Possedute" },
  { id: "mancanti", label: "Mancanti" },
  { id: "doppioni", label: "Doppioni" },
];
const RARITIES = ["C", "UC", "R", "SR", "SEC", "L", "P"];

export default function CollezioneScreen({ sets, imagesEnabled, refreshKey }) {
  const [overview, setOverview] = useState(null);
  const [setId, setSetId] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("tutte");
  const [rarity, setRarity] = useState("all");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/collection/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [refreshKey]);

  useEffect(() => {
    if (!setId && sets.length) setSetId(sets[0].set_id);
  }, [sets, setId]);

  useEffect(() => {
    if (!setId) return;
    setLoading(true);
    api.get(`/sets/${setId}/cards`).then((r) => setCards(r.data.cards)).finally(() => setLoading(false));
  }, [setId, refreshKey]);

  const perSet = useMemo(() => {
    const m = {};
    (overview?.per_set || []).forEach((s) => (m[s.set_id] = s));
    return m;
  }, [overview]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cards.filter((c) => {
      if (rarity !== "all" && c.rarity !== rarity) return false;
      if (status === "possedute" && !(c.quantity > 0)) return false;
      if (status === "mancanti" && c.quantity > 0) return false;
      if (status === "doppioni" && !(c.quantity > 1)) return false;
      if (q && !(c.name.toLowerCase().includes(q) || c.number.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [cards, rarity, status, query]);

  const g = overview?.global;
  const sp = perSet[setId];
  const selectedSet = sets.find((s) => s.set_id === setId);

  return (
    <div className="px-4 pt-4" data-testid="screen-collezione">
      <h2 className="font-display font-bold text-2xl mb-3">Il tuo raccoglitore</h2>

      {g && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[
            ["Numeri carta", `${g.unique_cards_owned}/${g.total_cards}`],
            ["Artwork", `${g.unique_variants_owned}/${g.total_variants}`],
            ["Doppioni", g.duplicates],
          ].map(([k, v]) => (
            <div key={k} className="luma-card !rounded-2xl p-2.5 text-center">
              <div className="font-display font-bold text-base leading-tight">{v}</div>
              <div className="text-[10px] luma-muted font-bold uppercase tracking-wide mt-0.5">{k}</div>
            </div>
          ))}
        </div>
      )}

      {/* Expansion select */}
      <div className="luma-card !rounded-2xl p-3 mb-3">
        <label className="text-[11px] font-bold uppercase tracking-wider luma-muted flex items-center gap-1.5 mb-1.5">
          <Layers size={14} /> Espansione
        </label>
        <select
          value={setId || ""}
          onChange={(e) => setSetId(e.target.value)}
          data-testid="collection-set-select"
          className="w-full bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5 font-bold text-sm outline-none"
        >
          {sets.map((s) => (
            <option key={s.set_id} value={s.set_id}>
              [{FAMILY_LABEL[s.family] || s.family}] {s.code} — {s.name.split("[")[0].trim()}
            </option>
          ))}
        </select>
        {sp && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between text-xs font-bold mb-1">
              <span className="luma-muted">Numeri {sp.owned_cards}/{sp.total_cards}</span>
              <span className="luma-muted">Artwork {sp.owned_variants}/{sp.total_variants}</span>
            </div>
            <div className="h-2 rounded-full bg-sky-100 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500" style={{ width: `${sp.total_cards ? (sp.owned_cards / sp.total_cards) * 100 : 0}%` }} />
            </div>
            {selectedSet && !selectedSet.openable && (
              <p className="text-[11px] luma-muted mt-1.5 font-semibold">Categoria da collezione: non apribile come bustina.</p>
            )}
          </div>
        )}
      </div>

      {/* Search + filters */}
      <div className="relative mb-2.5">
        <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 luma-muted" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca per nome o numero…"
          data-testid="collection-search"
          className="w-full bg-white border border-sky-200 rounded-full pl-10 pr-4 py-2.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-purple-200"
        />
      </div>

      <div className="flex gap-1.5 mb-2 overflow-x-auto no-scrollbar">
        {STATUS.map((s) => (
          <button key={s.id} onClick={() => setStatus(s.id)} data-testid={`filter-status-${s.id}`}
            className={`chip whitespace-nowrap ${status === s.id ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" : "bg-white border-sky-200"}`}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5 mb-4 overflow-x-auto no-scrollbar">
        <button onClick={() => setRarity("all")} className={`chip whitespace-nowrap ${rarity === "all" ? "bg-sky-500 text-white" : "bg-white border-sky-200"}`}>Tutte le rarità</button>
        {RARITIES.map((r) => {
          const meta = rarityMeta(r);
          return (
            <button key={r} onClick={() => setRarity(r)} data-testid={`filter-rarity-${r}`}
              className={`chip whitespace-nowrap ${rarity === r ? "bg-sky-500 text-white" : meta.cls}`}>{meta.label}</button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-center py-10 luma-muted font-semibold">Caricamento carte…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 luma-muted">
          <Copy size={34} className="mx-auto mb-2 opacity-50" />
          <p className="font-bold">Nessuna carta con questi filtri</p>
          <p className="text-sm">Apri qualche bustina per riempire il raccoglitore!</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5 pb-2">
          {filtered.map((c) => (
            <div key={c.variant_id} className={c.quantity > 0 ? "" : "opacity-45 grayscale"}>
              <CardFace card={c} imagesEnabled={imagesEnabled} onClick={() => setDetail(c)} />
            </div>
          ))}
        </div>
      )}

      {detail && <CardDetailModal card={detail} imagesEnabled={imagesEnabled} onClose={() => setDetail(null)} />}
    </div>
  );
}
